import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import {
  PaperPlaneTilt,
  Target,
  CursorClick,
  CheckCircle,
  ArrowsClockwise,
  CaretDown,
  CaretRight,
  WarningOctagon,
  TrendUp,
} from "@phosphor-icons/react";

/**
 * EmailConversionPanel
 *
 * Single "which emails actually move the metric?" board. Pulls
 * /api/admin/kpis/email-conversions and renders one collapsible
 * section per email surface (Sunday reminder, D3/D5/D14/D30 activation,
 * Kickstart, Pro Insights, …).
 *
 * Conversion = clicker created a truck event within `window_hours` of
 * their FIRST click on a campaign link. The only number that proves
 * the email is moving the schedule-upload needle, not just clicks.
 *
 * Surfaces with zero clicks render collapsed with a small "no data
 * yet" hint so quiet campaigns don't bury the noisy ones.
 *
 * Color coding on the conversion-rate cell:
 *   - ≥ 25% → primary (the email is genuinely working)
 *   - ≥ 10% → bold neutral (worth tracking)
 *   - <10% → muted (probably dead weight; consider cutting)
 */
export default function EmailConversionPanel() {
  const [data, setData] = useState(null);
  const [windowHours, setWindowHours] = useState(48);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set(["weekly_reminder"]));

  const load = async (hrs = windowHours) => {
    setBusy(true);
    try {
      const r = await api.get(
        `/admin/kpis/email-conversions?window_hours=${hrs}`
      );
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load(windowHours);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dead-weight detection: surfaces with ≥ 10 unique clickers AND a
  // sub-5% conversion rate are surfaced as a "candidates to cut" tip.
  const deadWeight = useMemo(() => {
    if (!data?.surfaces) return [];
    return data.surfaces.filter(
      (s) =>
        (s.totals.unique_clickers || 0) >= 10 &&
        (s.totals.conversion_pct || 0) < 5
    );
  }, [data]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!data) {
    return (
      <section
        className="rounded-2xl border border-border bg-card p-5"
        data-testid="email-conversion-panel-loading"
      >
        <div className="text-sm text-muted-foreground">
          Loading email-conversion stats…
        </div>
      </section>
    );
  }

  const { surfaces = [], totals = {} } = data;
  const hasAnyClicks = (totals.clicks || 0) > 0;

  return (
    <section
      className="rounded-2xl border border-border bg-card overflow-hidden"
      data-testid="email-conversion-panel"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3 bg-gradient-to-r from-primary/5 to-transparent">
        <div className="flex items-center gap-3">
          <div className="grid place-items-center h-10 w-10 rounded-xl bg-primary/10">
            <Target weight="duotone" className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
              Email · Conversion KPI
            </div>
            <div className="font-display font-bold text-lg leading-tight">
              Clicks → Posts within {data.window_hours}h
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={windowHours}
            onChange={(e) => {
              const v = Number(e.target.value);
              setWindowHours(v);
              load(v);
            }}
            className="text-xs font-semibold border border-border rounded-md bg-background px-2 py-1"
            data-testid="email-conversion-window-select"
          >
            <option value={24}>24h</option>
            <option value={48}>48h</option>
            <option value={72}>72h</option>
            <option value={168}>7d</option>
          </select>
          <button
            type="button"
            onClick={() => load(windowHours)}
            disabled={busy}
            className="text-xs font-semibold text-muted-foreground hover:text-foreground transition inline-flex items-center gap-1"
            data-testid="email-conversion-refresh-btn"
          >
            <ArrowsClockwise
              weight="bold"
              className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Grand totals */}
      <div className="grid grid-cols-3 divide-x divide-border border-b border-border">
        <Stat
          icon={<CursorClick weight="duotone" className="h-4 w-4 text-primary" />}
          label="Total clicks"
          value={totals.clicks || 0}
          testid="email-conversion-total-clicks"
        />
        <Stat
          icon={<PaperPlaneTilt weight="duotone" className="h-4 w-4 text-primary" />}
          label="Unique clickers"
          value={totals.unique_clickers || 0}
          testid="email-conversion-unique-clickers"
        />
        <Stat
          icon={<CheckCircle weight="duotone" className="h-4 w-4 text-primary" />}
          label="Converted (posted)"
          value={totals.converted || 0}
          sub={`${totals.conversion_pct || 0}%`}
          testid="email-conversion-converted"
        />
      </div>

      {/* Dead-weight callout */}
      {deadWeight.length > 0 && (
        <div
          className="px-5 py-3 border-b border-border bg-amber-50/40 dark:bg-amber-950/15 flex items-start gap-3"
          data-testid="email-conversion-dead-weight"
        >
          <WarningOctagon
            weight="duotone"
            className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5"
          />
          <div className="text-xs leading-relaxed text-amber-900 dark:text-amber-100">
            <span className="font-semibold">Candidates to cut:</span>{" "}
            {deadWeight.map((s) => s.label).join(", ")}
            {" "} — each has ≥10 clickers but is converting below 5%.
            Could be costing you Resend volume + recipient attention with
            little payoff.
          </div>
        </div>
      )}

      {/* Per-surface breakdown */}
      <div className="divide-y divide-border">
        {!hasAnyClicks ? (
          <div
            className="p-8 text-center text-sm text-muted-foreground"
            data-testid="email-conversion-empty"
          >
            No tracked email clicks yet. After your next send (or
            force-send), per-surface conversion will populate here.
          </div>
        ) : (
          surfaces.map((s) => {
            const open = expanded.has(s.id);
            const hasClicks = (s.totals.clicks || 0) > 0;
            return (
              <div
                key={s.id}
                data-testid={`email-conversion-surface-${s.id}`}
              >
                <button
                  type="button"
                  onClick={() => hasClicks && toggle(s.id)}
                  disabled={!hasClicks}
                  className={`w-full px-5 py-4 flex items-center gap-3 text-left transition ${
                    hasClicks ? "hover:bg-muted/40" : "opacity-50 cursor-default"
                  }`}
                >
                  <div className="shrink-0">
                    {hasClicks ? (
                      open ? (
                        <CaretDown weight="bold" className="h-4 w-4 text-primary" />
                      ) : (
                        <CaretRight weight="bold" className="h-4 w-4 text-primary" />
                      )
                    ) : (
                      <CaretRight weight="bold" className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{s.label}</div>
                    {!hasClicks && (
                      <div className="text-[11px] text-muted-foreground">
                        No clicks recorded yet
                      </div>
                    )}
                  </div>
                  <RateCell rate={s.totals.conversion_pct} />
                  <div className="text-right min-w-[80px]">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                      Posted
                    </div>
                    <div className="font-display font-bold text-base tabular-nums">
                      {s.totals.converted}
                      <span className="text-muted-foreground font-normal text-[11px]">
                        /{s.totals.unique_clickers}
                      </span>
                    </div>
                  </div>
                  <div className="text-right min-w-[64px]">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                      Clicks
                    </div>
                    <div className="font-display font-bold text-base tabular-nums">
                      {s.totals.clicks}
                    </div>
                  </div>
                </button>

                {open && hasClicks && (
                  <ul
                    className="px-5 pb-4 -mt-1 space-y-2"
                    data-testid={`email-conversion-rows-${s.id}`}
                  >
                    {s.campaigns.map((c) => (
                      <li
                        key={c.campaign}
                        className="flex items-center gap-3 rounded-xl border border-border bg-background/60 p-3"
                        data-testid={`email-conversion-row-${c.campaign}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{c.label}</div>
                          <div className="mt-0.5 text-[10px] text-muted-foreground font-mono truncate">
                            {c.campaign}
                          </div>
                        </div>
                        <div className="text-right min-w-[60px]">
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                            Clicks
                          </div>
                          <div className="font-display font-bold text-sm tabular-nums">
                            {c.clicks}
                          </div>
                        </div>
                        <div className="text-right min-w-[60px]">
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                            Posted
                          </div>
                          <div className="font-display font-bold text-sm tabular-nums">
                            {c.converted}/{c.unique_clickers}
                          </div>
                        </div>
                        <RateCell rate={c.conversion_pct} small />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="p-4 text-[11px] text-muted-foreground leading-relaxed border-t border-border bg-background/40">
        <TrendUp
          weight="duotone"
          className="h-3.5 w-3.5 inline -mt-0.5 mr-1 text-primary"
        />
        Conversion = clicker created a truck event within {data.window_hours}h
        of their FIRST click on a campaign link. Both manual posts and
        synced events count. To add a new email surface to this panel,
        edit <code>EMAIL_CONVERSION_SURFACES</code> in <code>server.py</code>.
      </div>
    </section>
  );
}

function Stat({ icon, label, value, sub, testid }) {
  return (
    <div className="px-5 py-4 flex items-start gap-3" data-testid={testid}>
      <div className="grid place-items-center h-8 w-8 rounded-lg bg-primary/5 shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
          {label}
        </div>
        <div className="font-display font-bold text-xl tabular-nums leading-tight">
          {value}
          {sub != null && (
            <span className="ml-2 text-sm font-semibold text-primary">{sub}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function RateCell({ rate, small = false }) {
  const tone =
    rate >= 25
      ? "text-primary"
      : rate >= 10
      ? "text-foreground"
      : "text-muted-foreground";
  const size = small ? "text-sm" : "text-base";
  return (
    <div className="text-right min-w-[60px]">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
        Rate
      </div>
      <div className={`font-display font-bold ${size} tabular-nums ${tone}`}>
        {rate}%
      </div>
    </div>
  );
}
